import { useEffect, useRef } from "react";
import { UseKeepSendingRequestConfig, UseKeepSendingRequestState } from "./utils/types";

const ONE_SECOND_IN_MS = 1000;

export const useKeepSendingRequest = (
	requestFunction: (sendingRequestState: UseKeepSendingRequestState) => any,
	config?: UseKeepSendingRequestConfig
) => {
	const { customNumberOfRequests, interval } = config ?? {};
	const numberOfRequests = customNumberOfRequests ?? 10;
	const forceStopSendingRequests = useRef<boolean>(false);
	const isSendingRequests = useRef(false);

	const clearHookValues = () => {
		forceStopSendingRequests.current = false;
		isSendingRequests.current = false;
	};

	const waitForTimeout = (timeoutDuration: number) => {
		if (timeoutDuration <= 0) return;

		return new Promise((resolve) =>
			setTimeout(() => {
				resolve(null);
			}, timeoutDuration)
		);
	};

	const getTimeoutDuration = (requestStartTimestamp: number): number => {
		const currentTimestamp = performance.now();
		const requestDuration = currentTimestamp - requestStartTimestamp;
		const roundedRequestDuration = Math.round(requestDuration);

		return (interval ?? ONE_SECOND_IN_MS) - roundedRequestDuration;
	};

	const keepSendingRequests = async (firstRequestStartTimestamp: number) => {
		isSendingRequests.current = true;

		await waitForTimeout(getTimeoutDuration(firstRequestStartTimestamp));

		for (let requestsCounter = 2; requestsCounter <= numberOfRequests; requestsCounter++) {
			const allRequestsSent = requestsCounter === numberOfRequests;
			const requestStartTimestamp = performance.now();

			await requestFunction({
				allRequestsSent,
				isSendingRequests: isSendingRequests.current,
				forceStopSendingRequests,
			});

			if (forceStopSendingRequests.current || allRequestsSent) {
				clearHookValues();
				break;
			}

			await waitForTimeout(getTimeoutDuration(requestStartTimestamp));
		}
	};

	useEffect(() => {
		return () => {
			forceStopSendingRequests.current = true;
		};
	}, []);

	return { keepSendingRequests };
};
